import "@/App.css";
import { BrowserRouter, Routes, Route, useLocation, useParams, Navigate } from "react-router-dom";
import { Toaster } from "sonner";
import { AuraProvider } from "@/context/AuraContext";
import { AuthProvider, useAuth } from "@/context/AuthContext";
import { SolanaProvider } from "@/context/SolanaProvider";
import ProtectedRoute from "@/components/auth/ProtectedRoute";
import AppLayout from "@/components/app/AppLayout";
import { Logo } from "@/components/brand/Logo";
import Landing from "@/pages/Landing";
import Onboarding from "@/pages/Onboarding";
import Login from "@/pages/Login";
import ForgotPassword from "@/pages/ForgotPassword";
import ResetPassword from "@/pages/ResetPassword";
import VerifyEmail from "@/pages/VerifyEmail";
import AuthCallback from "@/pages/AuthCallback";
import ForYou from "@/pages/ForYou";
import Explore from "@/pages/Explore";
import Live from "@/pages/Live";
import LiveRoom from "@/pages/LiveRoom";
import FoundersChannel from "@/pages/FoundersChannel";
import LiveEvent from "@/pages/LiveEvent";
import Profile from "@/pages/Profile";
import PublicProfile from "@/pages/PublicProfile";
import Notifications from "@/pages/Notifications";
import Messages from "@/pages/Messages";
import CoinDemo from "@/pages/CoinDemo";
import AuramaxToken from "@/pages/AuramaxToken";
import Settings from "@/pages/Settings";
import Leaderboard from "@/pages/Leaderboard";
import PersonalCoinLaunch from "@/pages/PersonalCoinLaunch";
import Balance from "@/pages/Balance";
import Referrals from "@/pages/Referrals";
import Market from "@/pages/Market";
import Admin from "@/pages/Admin";
import PostPage from "@/pages/PostPage";
import Unsubscribe from "@/pages/Unsubscribe";
import Genesis from "@/pages/Genesis";
import Contact from "@/pages/Contact";
import Legal from "@/pages/Legal";
const TermsPage = () => <Legal kind="terms" />;
const PrivacyPage = () => <Legal kind="privacy" />;

// Public pages (/genesis, /contact); logged-in members get them inside the app shell.
function PublicRoute({ page: Page }) {
  const { user, ready } = useAuth();
  if (!ready || user === null) return <div className="grid min-h-screen place-items-center bg-am-bg"><div className="animate-pulse"><Logo size={56} /></div></div>;
  if (!user) return <Page />;
  return <AppLayout><Page /></AppLayout>;
}
const GenesisRoute = () => <PublicRoute page={Genesis} />;

// /u/:username → canonical /@username
function LegacyProfileRedirect() {
  const { username } = useParams();
  return <Navigate to={username === "me" ? "/u/me" : `/@${username}`} replace />;
}

// /@username: public profile when logged out, full app profile when logged in.
function HandleRoute() {
  const { handle } = useParams();
  const { user, ready } = useAuth();
  if (!handle || !handle.startsWith("@") || handle.length < 2) return <Navigate to="/home" replace />;
  const username = handle.slice(1).toLowerCase();
  if (!ready || user === null) return <div className="grid min-h-screen place-items-center bg-am-bg"><div className="animate-pulse"><Logo size={56} /></div></div>;
  if (!user) return <PublicProfile username={username} />;
  return <AppLayout><Profile usernameOverride={username} /></AppLayout>;
}

function AppRouter() {
  const location = useLocation();
  // Emergent Google OAuth returns to /home#session_id=... — handle it before ProtectedRoute.
  if (location.hash?.includes("session_id=")) return <AuthCallback />;

  return (
    <Routes>
      <Route path="/" element={<Landing />} />
      <Route path="/join" element={<Onboarding />} />
      <Route path="/login" element={<Login />} />
      <Route path="/forgot" element={<ForgotPassword />} />
      <Route path="/reset-password" element={<ResetPassword />} />
      <Route path="/verify-email" element={<VerifyEmail />} />
      <Route path="/email/unsubscribe" element={<Unsubscribe />} />
      <Route path="/genesis" element={<GenesisRoute />} />
      <Route path="/contact" element={<PublicRoute page={Contact} />} />
      <Route path="/terms" element={<PublicRoute page={TermsPage} />} />
      <Route path="/privacy" element={<PublicRoute page={PrivacyPage} />} />
      <Route element={<ProtectedRoute><AppLayout /></ProtectedRoute>}>
        <Route path="/home" element={<ForYou />} />
        <Route path="/explore" element={<Explore />} />
        <Route path="/live" element={<Live />} />
        <Route path="/live/founders" element={<FoundersChannel />} />
        <Route path="/live/events/:id" element={<LiveEvent />} />
        <Route path="/live/:id" element={<LiveRoom />} />
        <Route path="/leaderboard" element={<Leaderboard />} />
        <Route path="/launch" element={<PersonalCoinLaunch />} />
        <Route path="/u/me" element={<Profile />} />
        <Route path="/post/:id" element={<PostPage />} />
        <Route path="/notifications" element={<Notifications />} />
        <Route path="/messages" element={<Messages />} />
        <Route path="/coin" element={<CoinDemo />} />
        <Route path="/token" element={<AuramaxToken />} />
        <Route path="/balance" element={<Balance />} />
        <Route path="/referrals" element={<Referrals />} />
        <Route path="/market" element={<Market />} />
        <Route path="/admin" element={<Admin />} />
        <Route path="/settings" element={<Settings />} />
      </Route>
      <Route path="/u/:username" element={<LegacyProfileRedirect />} />
      <Route path="/:handle" element={<HandleRoute />} />
    </Routes>
  );
}

function App() {
  return (
    <div className="App">
      <AuthProvider>
        <SolanaProvider>
        <AuraProvider>
          <BrowserRouter>
            <AppRouter />
          </BrowserRouter>
          <Toaster
            theme="dark"
            position="top-center"
            toastOptions={{
              style: {
                background: "rgba(16,18,21,0.9)",
                border: "1px solid rgba(255,255,255,0.1)",
                color: "#fff",
                backdropFilter: "blur(18px)",
              },
            }}
          />
        </AuraProvider>
        </SolanaProvider>
      </AuthProvider>
    </div>
  );
}

export default App;
